import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE, getAuthToken } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Sparkles, X, Send, LifeBuoy } from 'lucide-react';

/**
 * Universal Ari floating widget — visible on EVERY authenticated page except
 * the dedicated /seek Ari chat (where Ari is already the full page).
 *
 * Aug 2026 §8 — Global Ari:
 *   Sends {pathname, active_scan_id, viewing_listing_id, viewing_collection_id}
 *   to the backend so Ari can reason about the user's current context — e.g.
 *   answer "which car did I look at?" from Vault, or "start a scan from
 *   Settings" without asking the user to re-state.
 */
const SUGGESTIONS = [
  "How do I run my first scan?",
  "Something's wrong with the Guided Scan Extension",
  "How do I cancel my subscription?",
  "Show me my saved listings",
];

let SESSION_ID = null;
const sessionId = () => {
  if (!SESSION_ID) {
    SESSION_ID = `float-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
  return SESSION_ID;
};

// Best-effort page-context extraction. Consumed by chat_stream.
const derivePageContext = (pathname) => {
  const ctx = { pathname };
  // /results/:scanId — extract active_scan_id
  const rm = pathname.match(/^\/results\/([a-f0-9]{24})/i);
  if (rm) ctx.active_scan_id = rm[1];
  // /vault/collections tab — the collection viewer will publish its own id
  // via window.__zitaContext. Widget picks that up as a fallback.
  const external = (typeof window !== 'undefined' && window.__zitaContext) || {};
  return { ...ctx, ...external };
};

export const AriWidget = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Widget is hidden on login/register/verify/reset/forgot pages and on the
  // dedicated /seek chat page (where the full Ari surface lives).
  const HIDDEN = ['/', '/login', '/register', '/verify', '/reset', '/forgot',
    '/terms', '/privacy', '/about', '/admin/login', '/seek'];
  if (!user) return null;
  if (HIDDEN.includes(pathname)) return null;
  if (pathname.startsWith('/admin')) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }]);
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/ari/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          session_id: sessionId(),
          text,
          attachments: [],
          // Aug 2026 §8 — page context.
          context: derivePageContext(pathname),
        }),
      });
      if (!res.ok || !res.body) {
        throw new Error('stream failed');
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      setMessages((m) => [...m, { role: 'assistant', text: '' }]);
      let acc = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value, { stream: true });
        for (const line of chunk.split('\n')) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6);
          if (payload === '[DONE]') continue;
          acc += payload;
          setMessages((m) => {
            const clone = [...m];
            clone[clone.length - 1] = { role: 'assistant', text: acc };
            return clone;
          });
        }
      }
    } catch (e) {
      setMessages((m) => [...m, {
        role: 'assistant',
        text: "I hit a snag talking to my brain. If this keeps happening, tap 'File a ticket' below and I'll route it to the Zita team.",
      }]);
    } finally {
      setBusy(false);
    }
  };

  const fileTicket = () => {
    setOpen(false);
    navigate('/support');
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="ari-widget-toggle"
        aria-label={open ? 'Close Ari' : 'Open Ari'}
        className={`fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-transform ${
          open ? 'bg-zita-espresso text-zita-cream rotate-90' : 'bg-ari text-white hover:scale-105'
        }`}
      >
        {open ? <X size={22} /> : <Sparkles size={22} />}
      </button>

      {open && (
        <div
          data-testid="ari-widget-panel"
          className="fixed bottom-24 right-5 z-40 w-[92vw] sm:w-[380px] max-h-[70vh] zita-card p-0 overflow-hidden flex flex-col"
        >
          <div className="px-4 py-3 flex items-center gap-3 border-b border-zita-taupe/60">
            <div className="w-9 h-9 rounded-full bg-ari flex items-center justify-center text-white">
              <Sparkles size={16} />
            </div>
            <div className="flex-1">
              <div className="font-serif text-lg ari-accent">Ari</div>
              <div className="text-[10px] uppercase tracking-wider text-zita-mocha">
                Zita AI · first line of support
              </div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="space-y-3">
                <div className="text-sm text-zita-mocha">
                  Hi {user.display_name || 'there'} — I'm Ari. Ask me anything about Zita,
                  general questions, or troubleshooting.
                </div>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setInput(s)}
                      className="text-xs px-2 py-1 rounded-full bg-zita-sand text-zita-cocoa hover:bg-zita-sand/80"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`p-3 rounded-2xl text-sm ${
                  m.role === 'user'
                    ? 'bg-zita-espresso text-zita-cream ml-8'
                    : 'bg-ari/10 mr-8'
                }`}
              >
                <div className="whitespace-pre-wrap">{m.text || (m.role === 'assistant' ? '…' : '')}</div>
              </div>
            ))}
          </div>

          <div className="border-t border-zita-taupe/60 p-3 space-y-2">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
                placeholder="Ask Ari anything…"
                data-testid="ari-widget-input"
                disabled={busy}
                className="zita-ari-input flex-1 px-4 py-2 text-sm"
              />
              <button
                type="button"
                onClick={send}
                disabled={busy || !input.trim()}
                data-testid="ari-widget-send"
                className="zita-ari-send disabled:opacity-50"
              >
                <Send size={16} />
              </button>
            </div>
            <button
              type="button"
              onClick={fileTicket}
              data-testid="ari-widget-file-ticket"
              className="w-full text-xs text-zita-mocha hover:text-zita-cocoa flex items-center justify-center gap-1 py-1"
            >
              <LifeBuoy size={12} /> File a support ticket
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AriWidget;
